<app-page-shell>
    <p-toast [life]="10000"></p-toast>

    <app-page-hero eyebrow="Accounting" [title]="pageTitle" [subtitle]="pageSubtitle">
        <div actions>
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" type="button"
                [disabled]="isSaving" (onClick)="cancel()"></p-button>
            <p-button label="Save" icon="pi pi-check" type="button" [loading]="isSaving"
                [disabled]="isLoading || isSaving || !canSave || journalEntryForm.invalid || isTotalMismatch()"
                (onClick)="saveJournalEntry()"></p-button>
        </div>
    </app-page-hero>

    @if (isLoading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <form [formGroup]="journalEntryForm" (ngSubmit)="saveJournalEntry()" class="journal-entry-form">
        <app-page-form-section icon="pi pi-book" title="Journal Details"
            subtitle="Header information used for posting, workflow, and audit traceability.">
            <div class="formgrid grid">
                <div class="field col-12 md:col-6">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="entryNumber" type="text" pInputText formControlName="entryNumber" readonly
                                class="p-disabled" />
                            <label for="entryNumber">Entry Number</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field col-12 md:col-6">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="reference" type="text" pInputText formControlName="reference" />
                            <label for="reference">Reference / Description</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field col-12 md:col-6">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select id="journal" [options]="journalList" formControlName="journal"
                                optionLabel="journalName" styleClass="w-full"></p-select>
                            <label for="journal">Journal</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    @if (journalEntryForm.get('journal')?.invalid && journalEntryForm.get('journal')?.touched) {
                        <small class="p-error">Journal is required.</small>
                    }
                </div>
                <div class="field col-12 md:col-6">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker id="entryDate" formControlName="entryDate" dateFormat="yy-mm-dd"
                                [showIcon]="false" styleClass="w-full"></p-datePicker>
                            <label for="entryDate">Entry Date</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    @if (journalEntryForm.get('entryDate')?.invalid && journalEntryForm.get('entryDate')?.touched) {
                        <small class="p-error">Entry Date is required.</small>
                    }
                </div>
                <div class="field col-12 md:col-6">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                            <label for="logisticsProjectId">Logistics Project</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-list-check" title="Journal Lines"
            subtitle="Each line must post to an account with either debit or credit, and totals must balance.">
            <div actions class="line-toolbar">
                <p-button *ngIf="canSave" label="Add Line" icon="pi pi-plus" type="button"
                    (onClick)="addJournalEntryDetail()"></p-button>
            </div>

            <div formArrayName="journalItems" class="line-stack">
                @for (detailGroup of getJournalEntryDetailsControls; track $index; let i = $index) {
                    <section class="line-panel" [formGroupName]="i">
                        <div class="line-header">
                            <h3>Line {{ i + 1 }}</h3>
                            @if (getJournalEntryDetailsControls.length > 1) {
                                <p-button *ngIf="canSave" icon="pi pi-trash" severity="danger" [text]="true"
                                    [rounded]="true" type="button" (onClick)="removeJournalEntryDetail(i)"></p-button>
                            }
                        </div>

                        <div class="formgrid grid">
                            <div class="field col-12 md:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-autoComplete [suggestions]="filteredChartOfAccounts"
                                            (completeMethod)="filterChartOfAccounts($event)"
                                            formControlName="accountCode" id="accountCode{{ i }}"
                                            (onSelect)="onAccountSelect($event, i)" field="accountName"
                                            styleClass="w-full">
                                            <ng-template let-account pTemplate="item">
                                                <div>{{ account.accountCode }} - {{ account.accountName }}</div>
                                            </ng-template>
                                        </p-autoComplete>
                                        <label for="accountCode{{ i }}">Account</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                                @if (detailGroup.get('accountCode')?.invalid && detailGroup.get('accountCode')?.touched) {
                                    <small class="p-error">Account is required.</small>
                                }
                            </div>
                            <div class="field col-12 md:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select id="party{{ i }}" [options]="partyList" formControlName="party"
                                            optionLabel="entityName" [filter]="true" filterBy="entityName"
                                            [showClear]="true" [virtualScroll]="true" scrollHeight="200px" [virtualScrollItemSize]="38"
                                            styleClass="w-full">
                                            <ng-template let-party pTemplate="item">
                                                <div>{{ party.entityName }} ({{ party.id.entityType }})</div>
                                            </ng-template>
                                        </p-select>
                                        <label for="party{{ i }}">Party (optional)</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <app-logistics-project-lookup inputId="lineProject{{ i }}" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                                        <label for="lineProject{{ i }}">Line Project</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-down"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-inputNumber inputId="debitAmount{{ i }}" formControlName="debitAmount"
                                            mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="4" [min]="0"
                                            styleClass="w-full"></p-inputNumber>
                                        <label for="debitAmount{{ i }}">Debit</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-arrow-up"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-inputNumber inputId="creditAmount{{ i }}" formControlName="creditAmount"
                                            mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="4" [min]="0"
                                            styleClass="w-full"></p-inputNumber>
                                        <label for="creditAmount{{ i }}">Credit</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                        </div>

                        <div class="dimension-panel">
                            <div class="dimension-header">
                                <span>Dimensions</span>
                                <p-button *ngIf="canSave" label="Add Dimension" icon="pi pi-plus" [text]="true"
                                    type="button" (onClick)="addDimension(i)"></p-button>
                            </div>
                            <div formArrayName="dimensions" class="dimension-stack">
                                @for (dimensionCtrl of getDimensionsControls(i); track $index; let j = $index) {
                                    <div [formGroupName]="j" class="dimension-row">
                                        <p-select [options]="dimensionTypes" formControlName="dimensionTypeId"
                                            placeholder="Dimension" optionLabel="name" optionValue="dimensionTypeId"
                                            (onChange)="onDimensionTypeChange($event, i, j)" styleClass="w-full"></p-select>
                                        <p-select [options]="getDimensionOptions(i, j)" formControlName="dimensionValue"
                                            placeholder="Value" optionLabel="name" [filter]="true" filterBy="name"
                                            [virtualScroll]="true" styleClass="w-full"></p-select>
                                        <p-button *ngIf="canSave" icon="pi pi-trash" severity="danger" [text]="true"
                                            [rounded]="true" type="button" (onClick)="removeDimension(i, j)"></p-button>
                                    </div>
                                }
                            </div>
                        </div>
                    </section>
                }
            </div>

            @if (isTotalMismatch()) {
                <div class="balance-warning">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>Total debit and credit amounts must match before saving.</span>
                </div>
            }
        </app-page-form-section>
    </form>
</app-page-shell>
